import React from 'react';
import {Body, designSystemToken} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {
  NETWORK_CANONICAL_NAME,
  SOCIAL_NETWORK_DISPLAY_NAME,
} from '@/config/socialNetwork';
import ButtonGroup, {ButtonProps} from '@/components/button-group';
import CheckboxLabel from '@/components/checkbox-label';
import OptionButton from '@/components/option-button';
import SocialNetworkIcon from '@/components/social-network-icon';
import styles from './CloneNetworkSelector.module.scss';

const ICON_SIZE = 24;

const NETWORK_OPTIONS = [
  {
    value: 'instagram',
    label: SOCIAL_NETWORK_DISPLAY_NAME.instagram,
    iconName: 'instagram',
  },
  {
    value: 'tiktok',
    label: SOCIAL_NETWORK_DISPLAY_NAME.tiktok,
    iconName: 'tiktok',
  },
  {
    value: 'youtube',
    label: SOCIAL_NETWORK_DISPLAY_NAME.youtube,
    iconName: 'youtube',
  },
  {value: null, label: 'Content', iconName: null},
];

function toOptionValue(
  network: string | null | undefined
): string | null | undefined {
  if (network === undefined) return undefined;
  if (network === null) return null;
  return NETWORK_CANONICAL_NAME[network] ?? null;
}

interface CloneNetworkSelectorProps {
  selectedNetwork: string | null | undefined;
  sourceNetwork?: string | null;
  disabled?: boolean;
  onChange: (network: string | null | undefined) => void;
  onSelectorToggle?: (open: boolean) => void;
}

export default function CloneNetworkSelector({
  selectedNetwork,
  sourceNetwork,
  disabled = false,
  onChange,
  onSelectorToggle,
}: CloneNetworkSelectorProps) {
  const [showButtons, setShowButtons] = React.useState(
    selectedNetwork !== undefined
  );

  const handleCheckboxChange = (checked: boolean) => {
    setShowButtons(checked);
    onSelectorToggle?.(checked);
    if (!checked) {
      onChange(undefined);
    }
  };

  return (
    <div className={styles.container}>
      <CheckboxLabel
        checked={showButtons}
        onChange={handleCheckboxChange}
        label={translate('Clone to a different network')}
        disabled={disabled}
      />
      {showButtons && (
        <div className={styles.networkButtons}>
          <ButtonGroup
            containerClassName={styles.buttonGroup}
            buttons={NETWORK_OPTIONS.map((opt) => ({
              value: opt.value,
              label: opt.label,
              disabled: opt.value === toOptionValue(sourceNetwork) || disabled,
            }))}
            selectedButtonValue={selectedNetwork}
            onChange={(selection: ButtonProps | ButtonProps[]) =>
              onChange((selection as ButtonProps).value)
            }
            renderButton={(
              button: ButtonProps,
              isActive: boolean,
              onClick: () => void
            ) => (
              <OptionButton
                button={button}
                className={styles.networkButton}
                onClick={onClick}
                isActive={isActive}
              >
                <>
                  <SocialNetworkIcon
                    name={
                      button.value === null
                        ? null
                        : NETWORK_OPTIONS.find((o) => o.value === button.value)
                            ?.iconName
                    }
                    width={ICON_SIZE}
                    height={ICON_SIZE}
                  />
                  <span className={styles.networkLabel}>{button.label}</span>
                </>
              </OptionButton>
            )}
          />
          <Body
            size="sm"
            className={styles.deliverableNote}
            color={designSystemToken('semantic.fg.secondary')}
          >
            {translate(
              '* Deliverables may be adjusted to match the selected network and will need to be reviewed before publishing.'
            )}
          </Body>
        </div>
      )}
    </div>
  );
}
